import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Check, Award, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useCampaign } from "@/context/CampaignContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const { add, setOpen } = useCart();
  const { campaign, inCampaign } = useCampaign();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => setProduct(data));
  }, [id]);

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-[#9CA3AF]">…</div>;

  const name = lang === "ar" ? product.name_ar : product.name_en;
  const desc = lang === "ar" ? product.description_ar : product.description_en;
  const inStock = (product.stock ?? 1) > 0;

  const notifyMe = async () => {
    const email = window.prompt(lang === "ar" ? "بريدك الإلكتروني للتنبيه عند التوفر:" : "Your email for back-in-stock alert:");
    if (!email) return;
    try {
      const { api } = await import("@/lib/api");
      await api.post("/stock-notify", { product_id: product.id, email });
      toast.success(lang === "ar" ? "سنخبرك عند توفر المنتج" : "We'll notify you when available");
    } catch (e) { toast.error("Error"); }
  };

  const handleAdd = () => {
    add(product, qty);
    toast.success(t("addedToCart"), { description: name });
    setOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 page-enter">
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="aspect-square rounded-2xl overflow-hidden border border-[#27272A] bg-[#121214]">
          <img src={product.image_url} alt={name} className="w-full h-full object-cover" />
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3">{t(product.category)}</div>
          <h1 className="font-display text-3xl lg:text-5xl leading-tight mb-6" data-testid="product-title">
            {name}
          </h1>
          <div className="flex items-baseline gap-2 mb-8">
            <span className="font-display text-4xl text-[#D4AF37] num-ltr" data-testid="product-price">
              {product.price.toFixed(2)}
            </span>
            <span className="text-sm text-[#9CA3AF]">{t("currency")}</span>
          </div>

          {product.points > 0 && (
            <div className="flex items-center gap-2 mb-8 flex-wrap">
              <Link
                to="/rewards"
                data-testid="product-points-badge"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] text-sm hover:bg-[#D4AF37]/10 transition-colors num-ltr"
              >
                <Award size={16} />
                {t("earnPointsBadge").replace("{n}", inCampaign(product.id) ? Math.round(product.points * campaign.multiplier) : product.points)}
              </Link>
              {inCampaign(product.id) && (
                <span data-testid="product-campaign-badge" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#D4AF37] text-[#0A0A0B] text-xs font-bold num-ltr">
                  <Sparkles size={14} /> {lang === "ar" ? campaign.name_ar : campaign.name_en} ×{campaign.multiplier}
                </span>
              )}
            </div>
          )}

          <p className="text-[#F3F4F6]/80 leading-relaxed mb-8">{desc}</p>

          <div className="flex items-center gap-2 text-sm text-[#10B981] mb-8">
            <Check size={16} /> {t("free_shipping")}
          </div>

          <div className="divider-gold mb-8" />

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-[#27272A] rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease" className="p-3 hover:text-[#D4AF37]">
                <Minus size={16} />
              </button>
              <span className="num-ltr px-4 font-display text-lg">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} data-testid="qty-increase" className="p-3 hover:text-[#D4AF37]">
                <Plus size={16} />
              </button>
            </div>
            <button onClick={handleAdd} data-testid="product-add-to-cart-btn" className="btn-gold flex-1 py-3.5 rounded-full inline-flex items-center justify-center gap-2" disabled={!inStock}>
              <ShoppingBag size={18} />
              {inStock ? t("addToCart") : t("outOfStock")}
            </button>
          </div>
          {!inStock && (
            <button onClick={notifyMe} data-testid="notify-me-btn" className="btn-outline-gold w-full py-3 rounded-full">
              🔔 {lang === "ar" ? "أعلمني عند التوفّر" : "Notify me when available"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
